/**
 * Types et interfaces pour les Audit Logs
 */

// Enum types from backend
export type AuditAction =
  | 'USER_CREATED'
  | 'USER_UPDATED'
  | 'USER_DELETED'
  | 'USER_SUSPENDED'
  | 'USER_ACTIVATED'
  | 'USER_LOGIN'
  | 'USER_LOGOUT'
  | 'USER_PASSWORD_CHANGED'
  | 'PROFILE_CREATED'
  | 'PROFILE_UPDATED'
  | 'PROFILE_VERIFIED'
  | 'PROFILE_UNVERIFIED'
  | 'CARD_CREATED'
  | 'CARD_UPDATED'
  | 'CARD_DELETED'
  | 'CARD_PUBLISHED'
  | 'CARD_UNPUBLISHED'
  | 'CARD_SHARED'
  | 'PRODUCT_CREATED'
  | 'PRODUCT_UPDATED'
  | 'PRODUCT_DELETED'
  | 'PRODUCT_PUBLISHED'
  | 'ENTERPRISE_CREATED'
  | 'ENTERPRISE_UPDATED'
  | 'ENTERPRISE_SUSPENDED'
  | 'ENTERPRISE_DELETED'
  | 'ENTERPRISE_SETTINGS_UPDATED'
  | 'MEMBER_INVITED'
  | 'MEMBER_ACTIVATED'
  | 'MEMBER_UPDATED'
  | 'MEMBER_SUSPENDED'
  | 'MEMBER_DEPARTED'
  | 'MEMBER_ROLE_CHANGED'
  | 'ROLE_CREATED'
  | 'ROLE_UPDATED'
  | 'ROLE_DELETED'
  | 'ROLE_PERMISSIONS_CHANGED'
  | 'TICKET_CREATED'
  | 'TICKET_UPDATED'
  | 'TICKET_ASSIGNED'
  | 'TICKET_CLOSED'
  | 'TICKET_REOPENED'
  | 'CONTENT_APPROVED'
  | 'CONTENT_REJECTED'
  | 'CONTENT_FLAGGED'
  | 'SUBSCRIPTION_CREATED'
  | 'SUBSCRIPTION_UPGRADED'
  | 'SUBSCRIPTION_DOWNGRADED'
  | 'SUBSCRIPTION_CANCELLED'
  | 'PAYMENT_PROCESSED'
  | 'REFUND_ISSUED'
  | 'CONTACT_CREATED'
  | 'CONTACT_UPDATED'
  | 'CONTACT_DELETED'
  | 'CONTACT_EXPORTED'
  | 'SETTINGS_UPDATED'
  | 'PERMISSION_CHANGED'
  | 'EXPORT_GENERATED'
  | 'IMPORT_EXECUTED'
  | 'CUSTOM';

export type AuditModule =
  | 'PLATFORM_ADMIN'
  | 'ENTERPRISE'
  | 'USER'
  | 'API';

export interface AuditLogEntry {
  id: string;
  timestamp: Date;
  userId?: string;
  userEmail?: string;
  userRole?: string;
  userIp?: string;
  userAgent?: string;
  enterpriseId?: string;
  departmentId?: string;
  action: AuditAction;
  actionLabel?: string;
  resource: string;
  resourceId?: string;
  resourceLabel?: string;
  oldValue?: any;
  newValue?: any;
  module: AuditModule;
  endpoint?: string;
  method?: string;
  metadata?: any;
  success: boolean;
  errorMessage?: string;
}

export interface AuditLogFilter {
  userId?: string;
  enterpriseId?: string;
  action?: AuditAction;
  resource?: string;
  module?: AuditModule;
  success?: boolean;
  startDate?: Date;
  endDate?: Date;
  search?: string;
}

export interface AuditStats {
  totalActions: number;
  errorsCount: number;
  topModule: string;
  todayCount: number;
  actionDistribution: Record<string, number>;
}

// Labels français pour les actions (top 40)
export const ACTION_LABELS: Record<string, string> = {
  USER_CREATED: 'Utilisateur créé',
  USER_UPDATED: 'Utilisateur modifié',
  USER_DELETED: 'Utilisateur supprimé',
  USER_SUSPENDED: 'Utilisateur suspendu',
  USER_ACTIVATED: 'Utilisateur activé',
  USER_LOGIN: 'Connexion',
  USER_LOGOUT: 'Déconnexion',
  USER_PASSWORD_CHANGED: 'Mot de passe changé',
  PROFILE_CREATED: 'Profil créé',
  PROFILE_UPDATED: 'Profil modifié',
  PROFILE_VERIFIED: 'Profil vérifié',
  PROFILE_UNVERIFIED: 'Profil non vérifié',
  CARD_CREATED: 'Carte créée',
  CARD_UPDATED: 'Carte modifiée',
  CARD_DELETED: 'Carte supprimée',
  CARD_PUBLISHED: 'Carte publiée',
  CARD_UNPUBLISHED: 'Carte dépubliée',
  CARD_SHARED: 'Carte partagée',
  PRODUCT_CREATED: 'Produit créé',
  PRODUCT_UPDATED: 'Produit modifié',
  PRODUCT_DELETED: 'Produit supprimé',
  PRODUCT_PUBLISHED: 'Produit publié',
  ENTERPRISE_CREATED: 'Entreprise créée',
  ENTERPRISE_UPDATED: 'Entreprise modifiée',
  ENTERPRISE_SUSPENDED: 'Entreprise suspendue',
  ENTERPRISE_DELETED: 'Entreprise supprimée',
  ENTERPRISE_SETTINGS_UPDATED: 'Paramètres entreprise modifiés',
  MEMBER_INVITED: 'Membre invité',
  MEMBER_ACTIVATED: 'Membre activé',
  MEMBER_UPDATED: 'Membre modifié',
  MEMBER_SUSPENDED: 'Membre suspendu',
  MEMBER_DEPARTED: 'Membre parti',
  MEMBER_ROLE_CHANGED: 'Rôle membre changé',
  ROLE_CREATED: 'Rôle créé',
  ROLE_UPDATED: 'Rôle modifié',
  ROLE_DELETED: 'Rôle supprimé',
  ROLE_PERMISSIONS_CHANGED: 'Permissions rôle changées',
  TICKET_CREATED: 'Ticket créé',
  TICKET_UPDATED: 'Ticket modifié',
  TICKET_ASSIGNED: 'Ticket assigné',
  TICKET_CLOSED: 'Ticket fermé',
  TICKET_REOPENED: 'Ticket réouvert',
  CONTENT_APPROVED: 'Contenu approuvé',
  CONTENT_REJECTED: 'Contenu rejeté',
  CONTENT_FLAGGED: 'Contenu signalé',
  SUBSCRIPTION_CREATED: 'Abonnement créé',
  SUBSCRIPTION_UPGRADED: 'Abonnement upgradé',
  SUBSCRIPTION_DOWNGRADED: 'Abonnement downgradé',
  SUBSCRIPTION_CANCELLED: 'Abonnement annulé',
  PAYMENT_PROCESSED: 'Paiement traité',
  REFUND_ISSUED: 'Remboursement émis',
  CONTACT_CREATED: 'Contact créé',
  CONTACT_UPDATED: 'Contact modifié',
  CONTACT_DELETED: 'Contact supprimé',
  CONTACT_EXPORTED: 'Contacts exportés',
  SETTINGS_UPDATED: 'Paramètres modifiés',
  PERMISSION_CHANGED: 'Permission changée',
  EXPORT_GENERATED: 'Export généré',
  IMPORT_EXECUTED: 'Import exécuté',
  CUSTOM: 'Action personnalisée',
};

// Couleurs MUI pour les actions (par catégorie)
export const ACTION_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'> = {
  // Création (vert)
  USER_CREATED: 'success',
  PROFILE_CREATED: 'success',
  CARD_CREATED: 'success',
  PRODUCT_CREATED: 'success',
  ENTERPRISE_CREATED: 'success',
  ROLE_CREATED: 'success',
  TICKET_CREATED: 'success',
  CONTACT_CREATED: 'success',
  SUBSCRIPTION_CREATED: 'success',

  // Modification (bleu)
  USER_UPDATED: 'info',
  PROFILE_UPDATED: 'info',
  CARD_UPDATED: 'info',
  PRODUCT_UPDATED: 'info',
  ENTERPRISE_UPDATED: 'info',
  ENTERPRISE_SETTINGS_UPDATED: 'info',
  MEMBER_UPDATED: 'info',
  ROLE_UPDATED: 'info',
  TICKET_UPDATED: 'info',
  CONTACT_UPDATED: 'info',
  SETTINGS_UPDATED: 'info',
  PERMISSION_CHANGED: 'info',
  ROLE_PERMISSIONS_CHANGED: 'info',
  MEMBER_ROLE_CHANGED: 'info',
  USER_PASSWORD_CHANGED: 'info',

  // Suppression/Suspension (rouge)
  USER_DELETED: 'error',
  USER_SUSPENDED: 'error',
  CARD_DELETED: 'error',
  PRODUCT_DELETED: 'error',
  ENTERPRISE_DELETED: 'error',
  ENTERPRISE_SUSPENDED: 'error',
  MEMBER_SUSPENDED: 'error',
  ROLE_DELETED: 'error',
  CONTACT_DELETED: 'error',
  CONTENT_REJECTED: 'error',
  SUBSCRIPTION_CANCELLED: 'error',
  CARD_UNPUBLISHED: 'error',

  // Activation/Approbation (vert clair)
  USER_ACTIVATED: 'success',
  MEMBER_ACTIVATED: 'success',
  CONTENT_APPROVED: 'success',
  PROFILE_VERIFIED: 'success',

  // Connexion (primary)
  USER_LOGIN: 'primary',
  USER_LOGOUT: 'default',

  // Alertes (warning)
  CONTENT_FLAGGED: 'warning',
  PROFILE_UNVERIFIED: 'warning',
  MEMBER_DEPARTED: 'warning',

  // Paiements (secondary)
  PAYMENT_PROCESSED: 'secondary',
  REFUND_ISSUED: 'warning',
  SUBSCRIPTION_UPGRADED: 'success',
  SUBSCRIPTION_DOWNGRADED: 'warning',

  // Publications
  CARD_PUBLISHED: 'success',
  PRODUCT_PUBLISHED: 'success',
  CARD_SHARED: 'info',

  // Support
  TICKET_ASSIGNED: 'info',
  TICKET_CLOSED: 'success',
  TICKET_REOPENED: 'warning',

  // CRM/Export
  CONTACT_EXPORTED: 'info',
  EXPORT_GENERATED: 'info',
  IMPORT_EXECUTED: 'info',

  // Invitations
  MEMBER_INVITED: 'info',

  // Default
  CUSTOM: 'default',
};

// Labels modules
export const MODULE_LABELS: Record<AuditModule, string> = {
  PLATFORM_ADMIN: 'Admin',
  ENTERPRISE: 'Entreprise',
  USER: 'Utilisateur',
  API: 'API',
};

// Couleurs modules
export const MODULE_COLORS: Record<AuditModule, 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'> = {
  PLATFORM_ADMIN: 'error',
  ENTERPRISE: 'primary',
  USER: 'info',
  API: 'secondary',
};
